import { UserJourney } from './types'

/**
 * sessionStorage snapshot of the in-flight journey (CASHDAP-3407 restart reconciliation).
 *
 * Written on journey start and kept current on every journey action; cleared the moment
 * any completion is reported. A marker that survives to the next page load therefore
 * means the previous page died without reporting a terminal outcome (crash, or a
 * browser that never fired pagehide) — exactly the instrumentation gap the validation
 * guide says JOURNEY_RESTARTED represents. sessionStorage is per-tab and dies with the
 * tab, so a closed tab leaves nothing to reconcile (abandonment is derived analytically
 * from started − terminal) while a same-tab reload reconciles here. The esid rides
 * along so the reconciled completion reports under the same session as its started
 * event. Note the reconciled ended_at is reconcile-time, not death-time: after a true
 * crash the timeout timer never fires and the tab can sit for hours before a reload,
 * so JOURNEY_RESTARTED durations are unbounded upper estimates.
 */
export type JourneyMarker = {
  journey: UserJourney
  esid: string
  /**
   * Set just before an intentional cross-origin handoff (the Cash grant ceremony
   * redirect). Tells the next page load to resume this journey instead of reconciling
   * it as a restart, and tells reconciliation to leave it alone. Cleared by the next
   * regular marker write (any journey action, including the resume itself).
   */
  handoff?: boolean
}

const ACTIVE_JOURNEY_MARKER_KEY = 'apt-uj-active-journey'

// sessionStorage can throw (SSR, private modes, storage disabled); the journey must
// never fail because its marker could not be written. `handoff` must be re-asserted by
// writes that land between the handoff being marked and the page actually unloading
// (location.replace keeps the JS realm alive for the cross-origin round trip).
export const writeJourneyMarker = (journey: UserJourney, esid: string, handoff = false): void => {
  try {
    window.sessionStorage.setItem(ACTIVE_JOURNEY_MARKER_KEY, JSON.stringify({ journey, esid, ...(handoff ? { handoff: true } : {}) }))
  } catch {
    // marker is best-effort
  }
}

export const clearJourneyMarker = (): void => {
  try {
    window.sessionStorage.removeItem(ACTIVE_JOURNEY_MARKER_KEY)
  } catch {
    // marker is best-effort
  }
}

export const readJourneyMarker = (): JourneyMarker | undefined => {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_JOURNEY_MARKER_KEY)
    if (raw === null) {
      return undefined
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return undefined
    }
    const { journey, esid, handoff } = parsed as { journey?: Partial<UserJourney>; esid?: unknown; handoff?: unknown }
    if (typeof esid !== 'string' || typeof journey !== 'object' || journey === null) {
      return undefined
    }
    if (typeof journey.id !== 'string' || typeof journey.name !== 'string' || typeof journey.created !== 'number') {
      return undefined
    }
    return {
      esid,
      ...(handoff === true ? { handoff: true } : {}),
      journey: {
        id: journey.id,
        name: journey.name,
        created: journey.created,
        variant: typeof journey.variant === 'string' ? journey.variant : undefined,
        frustrations: Array.isArray(journey.frustrations) ? journey.frustrations : [],
        frictions: Array.isArray(journey.frictions) ? journey.frictions : [],
        tags: Array.isArray(journey.tags) ? journey.tags : [],
      },
    }
  } catch {
    return undefined
  }
}

/** Flags the current marker as an intentional handoff; no-ops when no marker exists */
export const markJourneyMarkerHandoff = (): void => {
  try {
    const marker = readJourneyMarker()
    if (marker !== undefined) {
      window.sessionStorage.setItem(ACTIVE_JOURNEY_MARKER_KEY, JSON.stringify({ ...marker, handoff: true }))
    }
  } catch {
    // marker is best-effort
  }
}
